import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { HiOutlineBars3, HiChevronDown, HiChevronRight, HiOutlineUser, HiOutlineArrowRightOnRectangle } from 'react-icons/hi2';

import { userAtom } from '../../services/atoms/auth.atoms';
import { useAuthActions } from '../../services/actions/auth.actions';
import { ROUTES } from '../../helpers/constants';
import { SidebarMenuCard } from '../../types/types';
import { GlobalSearch } from './global-search';

interface props {
  toggle: () => void;
  card: SidebarMenuCard;
}

interface Crumb {
  label: string;
  to?: string;
}

const initials = (name = '') => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || 'GM';
};

const fullName = (person: any) => [person?.name, person?.surname].filter(Boolean).join(' ');

// Migas de la barra superior: la sección en la que se está y, cuando la pantalla
// los recibe por navegación, el nombre del cliente y del colaborador.
const buildCrumbs = (pathname: string, isSuperAdmin: boolean, state: any): Crumb[] => {
  const clientsLabel = isSuperAdmin ? 'Clientes' : 'Mi empresa';
  const section = (label: string, route: string, child?: string): Crumb[] =>
    child ? [{ label, to: route }, { label: child }] : [{ label }];
  const client = state?.client;
  const clientRoute = client?.id ? `${ROUTES.CLIENTS.route}/${client.id}` : ROUTES.CLIENTS.route;

  if (pathname.startsWith(ROUTES.HOME.route)) return [{ label: 'Inicio' }];
  if (pathname.startsWith(`${ROUTES.CLIENTS.route}/collaborator`)) {
    const crumbs: Crumb[] = [{ label: clientsLabel, to: ROUTES.CLIENTS.route }];
    if (isSuperAdmin && client?.name) crumbs.push({ label: client.name, to: clientRoute });
    crumbs.push({ label: fullName(state?.collaborator) || 'Colaborador' });
    return crumbs;
  }
  if (pathname.startsWith(`${ROUTES.CLIENTS.route}/`)) {
    return isSuperAdmin ? section(clientsLabel, ROUTES.CLIENTS.route, client?.name || 'Cliente') : [{ label: clientsLabel }];
  }
  if (pathname.startsWith(ROUTES.CLIENTS.route)) return [{ label: clientsLabel }];
  if (pathname.startsWith(`${ROUTES.PROCESS.route}/create-view`)) return section('Evaluaciones', ROUTES.PROCESS.route, 'Nueva evaluación');
  if (pathname.startsWith(`${ROUTES.PROCESS.route}/collaborator`)) return section('Evaluaciones', ROUTES.PROCESS.route, fullName(state?.collaborator) || 'Colaborador');
  if (pathname.startsWith(`${ROUTES.PROCESS.route}/`)) return section('Evaluaciones', ROUTES.PROCESS.route, 'Detalle');
  if (pathname.startsWith(ROUTES.PROCESS.route)) return [{ label: 'Evaluaciones' }];
  if (pathname.startsWith(`${ROUTES.PARAMS.route}/`)) return section('Parámetros', ROUTES.PARAMS.route, 'Perfil profesional');
  if (pathname.startsWith(ROUTES.PARAMS.route)) return [{ label: 'Parámetros' }];
  if (pathname.startsWith(ROUTES.CONFIGURATION.route)) return [{ label: isSuperAdmin ? 'Configuraciones' : 'Mi perfil' }];
  if (pathname.startsWith(ROUTES.PROFILE.route)) return [{ label: 'Mi perfil' }];
  if (pathname.startsWith(ROUTES.USERS.route)) return [{ label: 'Usuarios' }];
  if (pathname.startsWith(ROUTES.GUEST_PAYMENTS.route)) return [{ label: 'Pagos por asignar' }];
  if (pathname.startsWith(ROUTES.PURCHASE.route)) return [{ label: 'Comprar tests' }];
  if (pathname.startsWith(ROUTES.CHECKOUT.route)) return section('Comprar tests', ROUTES.PURCHASE.route, 'Pago');
  if (pathname.startsWith(ROUTES.PAYMENT_SUCCESS.route) || pathname.startsWith(ROUTES.PAYMENT_FAILURE.route)) return [{ label: 'Resultado del pago' }];
  return [];
};

export const NavbarMenu: React.FC<props> = ({ toggle }) => {
  const navigate = useNavigate();
  const { pathname, state } = useLocation();
  const userData = useRecoilValue(userAtom);
  const { signOut } = useAuthActions();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const user = userData?.data || {};
  const crumbs = buildCrumbs(pathname, !!user.isSuperAdmin, state);
  const roleLabel = user.isSuperAdmin ? 'Superadmin · GrowMe' : user.isAdminBasic ? 'Usuario operativo' : 'Administrador';

  useEffect(() => {
    if (!menuOpen) return undefined;
    const close = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) setMenuOpen(false);
    };
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  useEffect(() => { setMenuOpen(false); }, [pathname]);

  const goToProfile = () => {
    try { localStorage.setItem('viewSelected', 'profile'); } catch { /* sin almacenamiento */ }
    setMenuOpen(false);
    navigate(ROUTES.CONFIGURATION.route);
  };

  return (
    <header id='navbar' className="gm-topbar">
      <button type="button" className="gm-icon-btn gm-topbar-burger" onClick={toggle} aria-label="Abrir menú">
        <HiOutlineBars3 size={22} />
      </button>
      <nav className="gm-crumbs" aria-label="Ubicación">
        {crumbs.map((crumb, index) => (
          <span key={`${crumb.label}-${index}`} className="gm-crumb">
            {index > 0 && <HiChevronRight size={14} className="gm-crumb-sep" />}
            {crumb.to
              ? <Link to={crumb.to}>{crumb.label}</Link>
              : <span className="gm-crumb-current">{crumb.label}</span>}
          </span>
        ))}
      </nav>
      <GlobalSearch isSuperAdmin={!!user.isSuperAdmin} />
      <div className="gm-topbar-right" ref={menuRef}>
        <button
          type="button"
          className="gm-user-btn"
          onClick={() => setMenuOpen((open) => !open)}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
        >
          <span className="gm-avatar">{initials(user.displayName || user.name)}</span>
          <span className="gm-user-who">
            <b>{user.displayName || user.name || 'Usuario'}</b>
            <span>{roleLabel}</span>
          </span>
          <HiChevronDown size={16} className="gm-user-chevron" />
        </button>
        {menuOpen && (
          <div className="gm-user-menu" role="menu">
            <div className="gm-user-menu-head">
              <b>{user.displayName || user.name || 'Usuario'}</b>
              <span>{user.email}</span>
            </div>
            <button type="button" role="menuitem" onClick={goToProfile}>
              <HiOutlineUser size={18} /> Mi perfil
            </button>
            <hr />
            <button type="button" role="menuitem" className="is-danger" onClick={() => signOut()}>
              <HiOutlineArrowRightOnRectangle size={18} /> Cerrar sesión
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
